import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Navigation from './components/Navigation';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import Dashboard from './pages/Dashboard';
import QuotePage from './pages/QuotePage';
import QuoteStepRedirect from './components/QuoteStepRedirect';
import ClaimPage from './pages/ClaimPage';
import ServicesPage from './pages/ServicesPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import ConnectionBanner from './components/pwa/ConnectionBanner';
import InstallPrompt from './components/pwa/InstallPrompt';
import { registerServiceWorker } from './pwa/registerServiceWorker';
import { Analytics } from '@vercel/analytics/react';
import './App.css';

/**
 * The app's front door.
 *
 * Signed in  -> straight to the dashboard.
 * Signed out -> the login screen renders AT "/" -- a rewrite, not a bounce.
 *               The URL never changes, so there is no flash of a marketing
 *               page and no extra history entry to back into.
 *
 * The loading gate matters: without it an authenticated visitor sees the login
 * form for however long the session takes to resolve, which reads as "you have
 * been signed out".
 */
const RootEntry = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <div className="text-gray-600">Loading...</div>
        </div>
      </div>
    );
  }

  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return <LoginPage />;
};

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <div className="text-gray-600">Loading...</div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function App() {
  const [updateRegistration, setUpdateRegistration] = useState(null);

  useEffect(() => {
    registerServiceWorker({ onUpdateReady: setUpdateRegistration });
  }, []);

  return (
    <AuthProvider>
      <Router>
        <div className="app-container">
          <Navigation />
          <ConnectionBanner updateRegistration={updateRegistration} />
          <Routes>
            <Route path="/" element={<RootEntry />} />
            <Route path="/quote" element={<QuotePage />} />
            <Route path="/quote/*" element={<QuoteStepRedirect />} />
            {/* Staff-issued invite link. Public: the visitor may not have an
                account yet. The bind itself is server-side. */}
            <Route path="/claim" element={<ClaimPage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route
              path="/dashboard/*"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
          </Routes>
          <InstallPrompt />
          {/* Vercel Web Analytics: page views only, no cookies, no cross-site
              identifiers. Inert until Web Analytics is enabled for the project. */}
          <Analytics />
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
